import { useProduce, ChangeDispatch } from "./utils";

export type PrimaryKey = number;

export type Entry<T> = T & { id: PrimaryKey };

export interface EntryTableState<T> {
  autoID: PrimaryKey,
  entries: {
    [priKey: number]: Entry<T>
  },
}

export interface EntryTableDispatch<T> {
  /**
   * 插入数据
   */
  insert: (...entries: T[]) => void;

  /**
   * 通过id删除数据
   */
  removeById: (id: PrimaryKey) => void;

  /**
   * 通过过滤器删除数据
   */
  removeByFilter: (filter: (item: Entry<T>) => boolean) => void;

  /**
   * 修改数据
   */
  updateById: (id: PrimaryKey, value: T) => void;

  /**
   * 通过mapper更新数据
   */
  updateByMapper: (mapper: (item: Entry<T>) => Entry<T>) => void;

}

export type EntryTableStore<T> = { table: EntryTableState<T>, entries: Entry<T>[] } & EntryTableDispatch<T>;

type Dispatch<T> = (changeFn: (darft: EntryTableState<T>) => void) => void;

const insert = <T>(dispatch: Dispatch<T>) => (...entries: T[]) => dispatch(
  draft => {
    entries.forEach(entry => {
      const id = draft.autoID;
      draft.entries[id] = { ...entry, id };
      draft.autoID++;
    });
  }
);

const removeById = <T>(dispatch: Dispatch<T>) => (id: PrimaryKey) => dispatch(draft => {
  if (id in draft.entries) {
    delete draft.entries[id];
  } else {
    throw new Error('not found');
  }
});

const updateById = <T>(dispatch: Dispatch<T>) => (id: PrimaryKey, value: T) => dispatch(draft => {
  if (id in draft.entries) {
    draft.entries[id] = { ...value, id };
  } else {
    throw new Error('not found');
  }
});

const removeByFilter = <T>(dispatch: Dispatch<T>) => (filter: (item: Entry<T>) => boolean) => dispatch(draft => {
  draft.entries = Object.values(draft.entries)
    .filter(item => !filter(item))
    .reduce((item, sum) => ({
      [item.id]: item,
      ...sum,
    }));
});

const updateByMapper = <T>(dispatch: Dispatch<T>) => (mapper: (item: Entry<T>) => Entry<T>) => dispatch(draft => {
  draft.entries = Object.values(draft.entries)
    .map(mapper)
    .reduce((item, sum) => ({
      [item.id]: item,
      ...sum,
    }));
})


export function useEntryTable<T>(): EntryTableStore<T> {
  const [table, dispatch] = useProduce<EntryTableState<T>>({
    autoID: 1,
    entries: {},
  });
  return {
    table,
    entries: Object.values(table.entries),
    insert: insert<T>(dispatch),
    removeById: removeById<T>(dispatch),
    updateById: updateById<T>(dispatch),
    removeByFilter: removeByFilter<T>(dispatch),
    updateByMapper: updateByMapper<T>(dispatch),
  };
}





